import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader"; // Adjust path as needed
import { get, post } from "helpers/api_helper"; // Adjust path as needed
import { GET_ALL_INTERVIEWS } from "constants/api_constants";
import CustomPagination from "components/CustomPagination";

const Interviews = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("upcoming"); // Default to "upcoming"
  const [interviews, setInterviews] = useState([]);
  console.log("interviews",interviews);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(""); // No default filter
  const [departmentFilter, setDepartmentFilter] = useState(""); // No default filter
  const [locationFilter, setLocationFilter] = useState(""); // No default filter
  const [searchQuery, setSearchQuery] = useState(""); // No default search
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // One-based for UI
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10; // Matches API response

  // Sync activeTab with statusFilter
  useEffect(() => {
    if (statusFilter === "Hired") {
      setActiveTab("completed");
    }
  }, [statusFilter]);

  // Fetch interviews from API with filters and pagination
  const fetchInterviews = useCallback(async () => {
    
    setLoading(true);
    setError(null);
    try {
      const payload = {
        pageSize,
        page: currentPage - 1, // Convert to zero-based for API
        department: departmentFilter || "",
        searchKey: searchQuery || "",
        location: locationFilter || "",
        status: statusFilter || "",
      };
      console.log("Fetching with payload:", payload); // Debugging
      const response = await post(GET_ALL_INTERVIEWS, payload);
      console.log("API response:", response); // Debugging
      const { success, message, data } = response;
      if (success && data && Array.isArray(data.data)) {
        const enrichedData = data.data.map((job, index) => ({
          ...job,
          id: job.id,
          jobId: job.jobId || `fallback-${index}`,
          department: job.department?.toLowerCase() || "unknown",
          location: job.location?.toLowerCase() || "unknown",
          status: job.status || "unknown",
        }));
        console.log("enrichedData",enrichedData);
        
        setInterviews(enrichedData);
        setTotalPages(
          data.totalPages || Math.ceil(data.totalItems / pageSize) || 1
        );
        setUniqueStatuses([...new Set(enrichedData.map((job) => job.status))]);
        setUniqueDepartments([
          ...new Set(enrichedData.map((job) => job.department)),
        ]);
        setUniqueLocations([
          ...new Set(enrichedData.map((job) => job.location)),
        ]);
      } else {
        throw new Error(message || "Failed to fetch interviews");
      }
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 405) {
        try {
          const queryParams = new URLSearchParams({
            pageSize,
            page: currentPage - 1, // Convert to zero-based for API
            department: departmentFilter || "",
            searchKey: searchQuery || "",
            location: locationFilter || "",
            status: statusFilter || "",
          }).toString();

          const getResponse = await get(`${GET_ALL_INTERVIEWS}?${queryParams}`);


          const { success, message, data } = getResponse;
          if (success && data && Array.isArray(data.data)) {
            const enrichedData = data.data.map((job, index) => ({
              ...job,
              jobId: job.jobId || `fallback-${index}`,
              department: job.department?.toLowerCase() || "unknown",
              location: job.location?.toLowerCase() || "unknown",
              status: job.status || "unknown",
            }));
            setInterviews(enrichedData);
            setTotalPages(
              data.totalPages || Math.ceil(data.totalItems / pageSize) || 1
            );
            setUniqueStatuses([
              ...new Set(enrichedData.map((job) => job.status)),
            ]);
            setUniqueDepartments([
              ...new Set(enrichedData.map((job) => job.department)),
            ]);
            setUniqueLocations([
              ...new Set(enrichedData.map((job) => job.location)),
            ]);
            return;
          }
        } catch (getErr) {
          console.error("GET Error:", getErr);
        }
      }
      setError(`Failed to fetch interviews: ${err.message}`);
      setInterviews([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    departmentFilter,
    searchQuery,
    locationFilter,
    statusFilter,
  ]);

  // Fetch data when filters or currentPage change
  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  // Filter data locally for tab-based filtering
  const filteredData = interviews.filter((job) => {
    const isHired = job.status === "Hired";
    return activeTab === "completed" ? isHired : job.status !== "Hired";
  });
  console.log("filteredData",filteredData);
  

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page); // Page is one-based from CustomPagination
  }, []);

  return (
    <div className="page-content">
      <PageSubHeader
        heading="Interviews"
        routePath="Home"
        pageName="Recruitment/ Interviews"
      />
      <Container fluid className="py-0 px-0">
        <Row className="px-4 gap">
          {/* Tabs */}
          <div
            className="d-flex align-items-center m-2"
            style={{ height: "56px", borderBottom: "1px solid #EAECF0" }}
          >
            <div
              className={`p-2 mb-0 nav-link-tab ${
                activeTab === "upcoming" ? "active" : ""
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab("upcoming")}
            >
              Upcoming
            </div>
            <div
              className={`p-2 mb-0 nav-link-tab ${
                activeTab === "completed" ? "active" : ""
              }`}
              style={{ marginLeft: 20, cursor: "pointer" }}
              onClick={() => setActiveTab("completed")}
            >
              Completed
            </div>
          </div>

          {/* Filters */}
          <Row className="px-4">
            <InputGroup
              size="sm"
              className="elbert-search-group"
              style={{ width: "348px" }}
            >
              <Input
                placeholder="Search by job title"
                className="elbert-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <InputGroupText>
                <span className="fa fa-search"></span>
              </InputGroupText>
            </InputGroup>

            <Input
              type="select"
              className="elbert-search"
              style={{ width: "178px", marginLeft: "16px" }}
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {uniqueDepartments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept.charAt(0).toUpperCase() + dept.slice(1)}
                </option>
              ))}
            </Input>

            <Input
              type="select"
              className="elbert-search"
              style={{ width: "125px", marginLeft: "16px" }}
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All Locations</option>
              {uniqueLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc.charAt(0).toUpperCase() + loc.slice(1)}
                </option>
              ))}
            </Input>

            <Input
              type="select"
              className="elbert-search"
              style={{ width: "125px", marginLeft: "16px" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Input>
          </Row>

          {/* Job Postings Table */}
          <Row className="mt-4 mx-0 px-4">
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : error ? (
                  <div className="text-center text-warning">{error}</div>
                ) : (
                  <div className="table-responsive">
                    <Table
                      className="table mb-0"
                      style={{ border: "0.4px solid #D4D4D4" }}
                    >
                      <thead>
                        <tr>
                          <th>Job ID</th>
                          <th>Job Title</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Current Stage</th>
                          <th>Interview Date & Time</th>
                          <th>Status</th>
                          <th>Department</th>
                          <th>Location</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length > 0 ? (
                          filteredData.map((job, index) => (
                            <tr key={job.jobId || `fallback-${index}`}>
                              <td>{job.jobId || "N/A"}</td>
                              <td>{job.jobTitle || "N/A"}</td>
                              <td>{job.name || "N/A"}</td>
                              <td>{job.email || "N/A"}</td>
                              <td>{job.interviewType || "N/A"}</td>
                              <td>{job.interviewDateAndTime || "N/A"}</td>
                              <td
                                style={{
                                  color:
                                    job.status === "PUT_ON_HOLD"
                                      ? "#FFA500"
                                      : job.status === "Hired"
                                      ? "#87CEEB"
                                      : job.status === "REJECT"
                                      ? "#EB7979"
                                      : "#006438",
                                }}
                              >
                                {job.status || "N/A"}
                              </td>
                              <td>{job.department || "N/A"}</td>
                              <td>{job.location || "N/A"}</td>
                              <td>
                                {" "}
                                <i
                                  onClick={() =>
                                    navigate(
                                      `/view-candidate-profile/${job.id}`
                                    )
                                  }
                                  className="fa-solid fa-eye action-icon"
                                  title="View"
                                ></i>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center">
                              No interviews found for the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
};

export default Interviews;
